/**
 * Per-splat property values for the data panel (histograms, select by range)
 * and colour matching. Definitions follow SuperSplat 3.5's splat-value shader:
 * positions are world space, distance is from the world origin, colours are
 * the graded display colour (DC + view-dependent SH toward the camera), and
 * hidden/deleted splats are excluded.
 */
import type { SplatEditModel } from '../SplatEditModel';
import { SH_C0, SH_COEFFS_PER_CHANNEL } from '../SplatEditModel';
import { createGradeTerms, type GradeTerms } from '../ColorGrade';

export type SplatPropId =
    | 'x' | 'y' | 'z' | 'distance' | 'cameraDepth'
    | 'red' | 'green' | 'blue' | 'opacity'
    | 'scale_0' | 'scale_1' | 'scale_2' | 'volume' | 'surfaceArea'
    | 'rot_0' | 'rot_1' | 'rot_2' | 'rot_3'
    | 'hue' | 'saturation' | 'value'
    | 'f_dc_0' | 'f_dc_1' | 'f_dc_2'
    | `f_rest_${number}`;

export const BASIC_PROPS: { id: SplatPropId; label: string; group: string }[] = [
    { id: 'x', label: 'X', group: 'Position' },
    { id: 'y', label: 'Y', group: 'Position' },
    { id: 'z', label: 'Z', group: 'Position' },
    { id: 'distance', label: 'Distance', group: 'Position' },
    { id: 'cameraDepth', label: 'Camera Depth', group: 'Position' },
    { id: 'red', label: 'Red', group: 'Colour' },
    { id: 'green', label: 'Green', group: 'Colour' },
    { id: 'blue', label: 'Blue', group: 'Colour' },
    { id: 'opacity', label: 'Opacity', group: 'Colour' },
    { id: 'hue', label: 'Hue', group: 'Colour' },
    { id: 'saturation', label: 'Saturation', group: 'Colour' },
    { id: 'value', label: 'Value', group: 'Colour' },
    { id: 'scale_0', label: 'Scale X', group: 'Shape' },
    { id: 'scale_1', label: 'Scale Y', group: 'Shape' },
    { id: 'scale_2', label: 'Scale Z', group: 'Shape' },
    { id: 'volume', label: 'Volume', group: 'Shape' },
    { id: 'surfaceArea', label: 'Surface Area', group: 'Shape' },
];

export const ADVANCED_PROPS: { id: SplatPropId; label: string; group: string }[] = [
    { id: 'rot_0', label: 'Quat W', group: 'Raw' },
    { id: 'rot_1', label: 'Quat X', group: 'Raw' },
    { id: 'rot_2', label: 'Quat Y', group: 'Raw' },
    { id: 'rot_3', label: 'Quat Z', group: 'Raw' },
    { id: 'f_dc_0', label: 'DC R', group: 'Raw' },
    { id: 'f_dc_1', label: 'DC G', group: 'Raw' },
    { id: 'f_dc_2', label: 'DC B', group: 'Raw' },
];

export interface PropContext {
    /** Column-major view matrix (for camera depth). */
    view?: ArrayLike<number>;
    /** Column-major view-projection (for on-screen-only). */
    viewProj?: ArrayLike<number>;
    /** World-space camera position (for view-dependent SH colour). */
    cameraPos?: { x: number; y: number; z: number };
    onScreenOnly?: boolean;
    /** Map values through sign(v)·log(1+|v|) before binning. */
    logBins?: boolean;
}

export interface PropValues {
    values: Float32Array;
    /** 1 when the splat takes part (selectable and, if requested, on screen). */
    include: Uint8Array;
}

export function signedLog1p(v: number): number {
    return v < 0 ? -Math.log1p(-v) : Math.log1p(v);
}

const sigmoid = (v: number) => 1 / (1 + Math.exp(-v));

/** Which splats take part in data-panel statistics. */
export function includedSplats(model: SplatEditModel, ctx: PropContext): Uint8Array {
    const n = model.numSplats;
    const include = new Uint8Array(n);
    const state = model.state;
    const vp = ctx.onScreenOnly ? ctx.viewProj : undefined;
    const centers = vp ? model.getWorldCenters() : null;
    for (let i = 0; i < n; i++) {
        if (state[i] & 6) continue;
        if (vp && centers) {
            const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
            const w = vp[3] * x + vp[7] * y + vp[11] * z + vp[15];
            if (w <= 0) continue;
            const nx = (vp[0] * x + vp[4] * y + vp[8] * z + vp[12]) / w;
            const ny = (vp[1] * x + vp[5] * y + vp[9] * z + vp[13]) / w;
            if (nx < -1 || nx > 1 || ny < -1 || ny > 1) continue;
        }
        include[i] = 1;
    }
    return include;
}

/**
 * Graded display colour of every splat (3 floats each, linear 0..1-ish, not
 * clamped). Includes view-dependent SH toward `cameraPos` when given.
 */
export function computeDisplayColors(model: SplatEditModel, cameraPos?: { x: number; y: number; z: number }): Float32Array {
    const n = model.numSplats;
    const out = new Float32Array(n * 3);
    const d0 = model.col('f_dc_0'), d1 = model.col('f_dc_1'), d2 = model.col('f_dc_2');
    for (let i = 0; i < n; i++) {
        out[i * 3] = d0[i] * SH_C0 + 0.5;
        out[i * 3 + 1] = d1[i] * SH_C0 + 0.5;
        out[i * 3 + 2] = d2[i] * SH_C0 + 0.5;
    }

    if (cameraPos && model.shBands > 0) addViewDependentSH(model, cameraPos, out);

    // colour grades
    const pal = model.colorPalette;
    if (pal.count > 1) {
        const terms: GradeTerms[] = [];
        for (let k = 0; k < pal.count; k++) terms.push(pal.getTerms(k, createGradeTerms()));
        const ci = model.colorIndex;
        for (let i = 0; i < n; i++) {
            const k = ci[i];
            if (k === 0) continue;
            const t = terms[k];
            const m = t.m;
            const r = out[i * 3], g = out[i * 3 + 1], b = out[i * 3 + 2];
            out[i * 3] = m[0] * r + m[3] * g + m[6] * b + t.offset.r;
            out[i * 3 + 1] = m[1] * r + m[4] * g + m[7] * b + t.offset.g;
            out[i * 3 + 2] = m[2] * r + m[5] * g + m[8] * b + t.offset.b;
        }
    }
    return out;
}

// real SH basis constants (bands 1..3), matching the engine's evalSH
const SH_C1 = 0.4886025119029199;
const SH_C2 = [1.0925484305920792, -1.0925484305920792, 0.31539156525252005, -1.0925484305920792, 0.5462742152960396];
const SH_C3 = [-0.5900435899266435, 2.890611442640554, -0.4570457994644658, 0.3731763325901154, -0.4570457994644658, 1.445305721320277, -0.5900435899266435];

/** Evaluate the SH basis (bands 1..3) for a unit direction into `out` (15 values). */
export function shBasis(x: number, y: number, z: number, out: Float32Array): void {
    out[0] = -SH_C1 * y;
    out[1] = SH_C1 * z;
    out[2] = -SH_C1 * x;
    const xx = x * x, yy = y * y, zz = z * z, xy = x * y, yz = y * z, xz = x * z;
    out[3] = SH_C2[0] * xy;
    out[4] = SH_C2[1] * yz;
    out[5] = SH_C2[2] * (2 * zz - xx - yy);
    out[6] = SH_C2[3] * xz;
    out[7] = SH_C2[4] * (xx - yy);
    out[8] = SH_C3[0] * y * (3 * xx - yy);
    out[9] = SH_C3[1] * xy * z;
    out[10] = SH_C3[2] * y * (4 * zz - xx - yy);
    out[11] = SH_C3[3] * z * (2 * zz - 3 * xx - 3 * yy);
    out[12] = SH_C3[4] * x * (4 * zz - xx - yy);
    out[13] = SH_C3[5] * z * (xx - yy);
    out[14] = SH_C3[6] * x * (xx - 3 * yy);
}

function addViewDependentSH(model: SplatEditModel, cam: { x: number; y: number; z: number }, out: Float32Array): void {
    const n = model.numSplats;
    const per = SH_COEFFS_PER_CHANNEL[model.shBands];
    const names = model.shColumnNames();
    const cols = names.map((nm) => model.col(nm));
    const centers = model.getWorldCenters();
    const combined = model.combinedTransforms();
    const ti = model.transformIndex;
    // inverse linear parts per palette entry (world → model direction)
    const invs: Float32Array[] = [];
    for (let k = 0; k < model.transformPalette.count; k++) invs.push(inverseLinear3(combined, k * 12));
    const basis = new Float32Array(15);
    for (let i = 0; i < n; i++) {
        let dx = centers[i * 3] - cam.x, dy = centers[i * 3 + 1] - cam.y, dz = centers[i * 3 + 2] - cam.z;
        const inv = invs[ti[i]];
        const mx = inv[0] * dx + inv[1] * dy + inv[2] * dz;
        const my = inv[3] * dx + inv[4] * dy + inv[5] * dz;
        const mz = inv[6] * dx + inv[7] * dy + inv[8] * dz;
        const len = Math.hypot(mx, my, mz) || 1;
        dx = mx / len; dy = my / len; dz = mz / len;
        shBasis(dx, dy, dz, basis);
        let r = 0, g = 0, b = 0;
        for (let k = 0; k < per; k++) {
            const bk = basis[k];
            r += cols[k][i] * bk;
            g += cols[per + k][i] * bk;
            b += cols[2 * per + k][i] * bk;
        }
        out[i * 3] += r;
        out[i * 3 + 1] += g;
        out[i * 3 + 2] += b;
    }
}

/** Inverse of the 3x3 linear part of a 3x4 row-major block, row-major result. */
function inverseLinear3(m: Float32Array, o: number): Float32Array {
    const a = m[o], b = m[o + 1], c = m[o + 2];
    const d = m[o + 4], e = m[o + 5], f = m[o + 6];
    const g = m[o + 8], h = m[o + 9], k = m[o + 10];
    const A = e * k - f * h, B = -(d * k - f * g), C = d * h - e * g;
    const det = a * A + b * B + c * C || 1;
    const r = new Float32Array(9);
    r[0] = A / det; r[1] = -(b * k - c * h) / det; r[2] = (b * f - c * e) / det;
    r[3] = B / det; r[4] = (a * k - c * g) / det; r[5] = -(a * f - c * d) / det;
    r[6] = C / det; r[7] = -(a * h - b * g) / det; r[8] = (a * e - b * d) / det;
    return r;
}

export function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d > 0) {
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h /= 6;
        if (h < 0) h += 1;
    }
    return [h, max > 0 ? d / max : 0, max];
}

/** Compute one property for every splat. */
export function computePropValues(model: SplatEditModel, prop: SplatPropId, ctx: PropContext = {}): PropValues {
    const n = model.numSplats;
    const include = includedSplats(model, ctx);
    const values = new Float32Array(n);
    const centers = model.getWorldCenters();

    const fill = (fn: (i: number) => number) => {
        for (let i = 0; i < n; i++) if (include[i]) values[i] = fn(i);
    };

    switch (prop) {
        case 'x': fill((i) => centers[i * 3]); break;
        case 'y': fill((i) => centers[i * 3 + 1]); break;
        case 'z': fill((i) => centers[i * 3 + 2]); break;
        case 'distance': fill((i) => Math.hypot(centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2])); break;
        case 'cameraDepth': {
            const v = ctx.view;
            if (!v) break;
            fill((i) => -(v[2] * centers[i * 3] + v[6] * centers[i * 3 + 1] + v[10] * centers[i * 3 + 2] + v[14]));
            break;
        }
        case 'red': case 'green': case 'blue': {
            const colors = computeDisplayColors(model, ctx.cameraPos);
            const c = prop === 'red' ? 0 : prop === 'green' ? 1 : 2;
            fill((i) => colors[i * 3 + c]);
            break;
        }
        case 'hue': case 'saturation': case 'value': {
            const colors = computeDisplayColors(model, ctx.cameraPos);
            const c = prop === 'hue' ? 0 : prop === 'saturation' ? 1 : 2;
            fill((i) => {
                const hsv = rgbToHsv(clamp01(colors[i * 3]), clamp01(colors[i * 3 + 1]), clamp01(colors[i * 3 + 2]));
                return c === 0 ? hsv[0] * 360 : hsv[c];
            });
            break;
        }
        case 'opacity': {
            const op = model.col('opacity');
            const pal = model.colorPalette;
            const ci = model.colorIndex;
            fill((i) => sigmoid(op[i]) * (ci[i] ? pal.data[ci[i] * 16 + 12] : 1));
            break;
        }
        case 'scale_0': case 'scale_1': case 'scale_2': {
            const s = model.col(prop);
            fill((i) => Math.exp(s[i]));
            break;
        }
        case 'volume': {
            const s0 = model.col('scale_0'), s1 = model.col('scale_1'), s2 = model.col('scale_2');
            fill((i) => Math.exp(s0[i] + s1[i] + s2[i]));
            break;
        }
        case 'surfaceArea': {
            const s0 = model.col('scale_0'), s1 = model.col('scale_1'), s2 = model.col('scale_2');
            fill((i) => Math.exp(2 * s0[i]) + Math.exp(2 * s1[i]) + Math.exp(2 * s2[i]));
            break;
        }
        default: {
            const col = model.columns.get(prop);
            if (col) {
                if (prop.startsWith('rot_')) {
                    const r0 = model.col('rot_0'), r1 = model.col('rot_1'), r2 = model.col('rot_2'), r3 = model.col('rot_3');
                    fill((i) => col[i] / (Math.hypot(r0[i], r1[i], r2[i], r3[i]) || 1));
                } else {
                    fill((i) => col[i]);
                }
            }
        }
    }

    if (ctx.logBins) {
        for (let i = 0; i < n; i++) if (include[i]) values[i] = signedLog1p(values[i]);
    }
    return { values, include };
}

function clamp01(v: number): number {
    return v < 0 ? 0 : v > 1 ? 1 : v;
}

export interface HistogramResult {
    min: number;
    max: number;
    numBins: number;
    selected: Uint32Array;
    unselected: Uint32Array;
    count: number;
    selectedCount: number;
}

/** Bin included values; counts are split by current selection. */
export function computeHistogram(model: SplatEditModel, pv: PropValues, numBins = 256): HistogramResult {
    const { values, include } = pv;
    const n = values.length;
    let min = Infinity, max = -Infinity, count = 0, selectedCount = 0;
    for (let i = 0; i < n; i++) {
        if (!include[i]) continue;
        const v = values[i];
        if (!Number.isFinite(v)) continue;
        if (v < min) min = v;
        if (v > max) max = v;
    }
    const selected = new Uint32Array(numBins);
    const unselected = new Uint32Array(numBins);
    if (min === Infinity) return { min: 0, max: 0, numBins, selected, unselected, count: 0, selectedCount: 0 };
    const range = max - min || 1;
    const state = model.state;
    for (let i = 0; i < n; i++) {
        if (!include[i]) continue;
        const v = values[i];
        if (!Number.isFinite(v)) continue;
        const bin = Math.min(numBins - 1, Math.floor(((v - min) / range) * numBins));
        if (state[i] & 1) { selected[bin]++; selectedCount++; } else { unselected[bin]++; }
        count++;
    }
    return { min, max, numBins, selected, unselected, count, selectedCount };
}

/** Mask of included splats whose value falls in bins [binStart, binEnd] of a histogram. */
export function selectHistogramRange(pv: PropValues, hist: HistogramResult, binStart: number, binEnd: number): Uint8Array {
    const lo = Math.min(binStart, binEnd), hi = Math.max(binStart, binEnd);
    const range = hist.max - hist.min || 1;
    const n = pv.values.length;
    const mask = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        if (!pv.include[i]) continue;
        const v = pv.values[i];
        if (!Number.isFinite(v)) continue;
        const bin = Math.min(hist.numBins - 1, Math.floor(((v - hist.min) / range) * hist.numBins));
        if (bin >= lo && bin <= hi) mask[i] = 1;
    }
    return mask;
}

/** Mask of included splats whose value lies within [lo, hi] (raw units, after logBins). */
export function selectValueRange(pv: PropValues, lo: number, hi: number): Uint8Array {
    const n = pv.values.length;
    const mask = new Uint8Array(n);
    const a = Math.min(lo, hi), b = Math.max(lo, hi);
    for (let i = 0; i < n; i++) {
        if (pv.include[i] && pv.values[i] >= a && pv.values[i] <= b) mask[i] = 1;
    }
    return mask;
}

/**
 * Colour match (eyedropper): select splats whose graded display colour is
 * within `threshold` of splat `ref` on every channel (0 = identical only,
 * 1 = everything), as in SuperSplat.
 */
export function selectColorMatch(
    model: SplatEditModel, ref: number, threshold: number,
    cameraPos?: { x: number; y: number; z: number },
): Uint8Array {
    const n = model.numSplats;
    const mask = new Uint8Array(n);
    if (ref < 0 || ref >= n) return mask;
    const colors = computeDisplayColors(model, cameraPos);
    const t = Math.max(0, Math.min(1, threshold));
    const r = clamp01(colors[ref * 3]), g = clamp01(colors[ref * 3 + 1]), b = clamp01(colors[ref * 3 + 2]);
    const state = model.state;
    for (let i = 0; i < n; i++) {
        if (state[i] & 6) continue;
        if (Math.abs(clamp01(colors[i * 3]) - r) <= t
            && Math.abs(clamp01(colors[i * 3 + 1]) - g) <= t
            && Math.abs(clamp01(colors[i * 3 + 2]) - b) <= t) {
            mask[i] = 1;
        }
    }
    return mask;
}
